import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowDownWideNarrow, ArrowUp, ArrowUpNarrowWide, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useMobil } from '../../lib/use-mobil';
import { IconButton } from '../ui/button';
import { Icon } from '../ui/icon';
import { NativeSelect } from '../ui/select';
import { Skeleton } from '../ui/skeleton';

export interface Column<T> {
  key: string;
  header: ReactNode;
  width?: number | string;
  /** `end` für Beträge. */
  align?: 'start' | 'end';
  /** Für Nummern, Beträge und Daten. */
  mono?: boolean;
  /** Für den Namen, die erste Spalte. */
  strong?: boolean;
  /** Zelle selbst zeichnen, z. B. <Status variant="plain">. Ohne `render` steht bei leerem Feld „–“. */
  render?: (row: T) => ReactNode;
  /** Sortierbar: am Rechner über den Spaltenkopf, am Handy über die Auswahl über der Liste. Braucht `sort` und `onSortChange`. */
  sortable?: boolean;
  /**
   * Platz am Handy (unter 768 px), wo statt der Tabelle eine Zeile je Eintrag steht:
   * `titel` stark oben, `status` und `unter` (mit · getrennt) in der Zeile darunter,
   * `wert` rechts, die wichtigste Zahl. Spalten ohne Angabe fehlen am Handy.
   */
  mobil?: 'titel' | 'status' | 'unter' | 'wert';
  /** Zelle am Handy anders zeichnen, z. B. nur der Firmenname statt Name und Zusatz. */
  renderMobil?: (row: T) => ReactNode;
}

export interface DataTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** Feld oder Funktion, die eine stabile Zeilen-ID liefert. Standard: `id`. */
  rowKey?: keyof T | ((row: T) => string);
  /** Markiert die offene Zeile in highlight-soft. */
  selectedKey?: string;
  onRowClick?: (row: T) => void;
  /** Meist ein EmptyState. */
  empty?: ReactNode;
  /** Nur am Rechner; am Handy wächst die Liste mit der Seite. */
  maxHeight?: number | string;
  /** Beschriftung der Tabelle für Screenreader. */
  label?: string;
  /** Aktuelle Sortierung, gesteuert. Die Daten sortiert die App (meist der Server). */
  sort?: DataTableSort;
  /** Klick auf einen Spaltenkopf: gleiche Spalte kehrt die Richtung um, neue Spalte beginnt aufsteigend. */
  onSortChange?: (sort: DataTableSort) => void;
  /** Lädt: Skeleton-Zeilen statt der Daten. */
  loading?: boolean;
  className?: string;
}

const LADEZEILEN = 5;

function kopfText<T>(c: Column<T>) {
  return typeof c.header === 'string' ? c.header : c.key;
}

/**
 * Datensätze in Zeilen: Kunden, Projekte, Angebote. Name als erste starke Spalte,
 * Status als `Status variant="plain"`, Beträge rechtsbündig in Mono, Datum TT.MM.JJJJ.
 * Unter 768 px eine Zeile je Eintrag statt der Tabelle (README „Mobil“).
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const mobil = useMobil();
  return mobil ? <DataListe {...props} /> : <Tabelle {...props} />;
}

function schluesselVon<T>(rowKey: DataTableProps<T>['rowKey'] = 'id' as keyof T) {
  return (r: T) => String(typeof rowKey === 'function' ? rowKey(r) : r[rowKey]);
}

function zelle<T>(r: T, c: Column<T>): ReactNode {
  if (c.render) return c.render(r);
  const wert = (r as Record<string, unknown>)[c.key] as ReactNode;
  return wert ?? '–';
}

function zeilenTasten<T>(onRowClick: ((row: T) => void) | undefined, r: T) {
  return onRowClick
    ? {
        tabIndex: 0,
        onClick: () => onRowClick(r),
        onKeyDown: (e: KeyboardEvent) => e.key === 'Enter' && e.target === e.currentTarget && onRowClick(r),
      }
    : {};
}

function Tabelle<T>({ columns, rows, rowKey, selectedKey, onRowClick, empty, maxHeight, label, sort, onSortChange, loading, className }: DataTableProps<T>) {
  const schluessel = schluesselVon(rowKey);
  return (
    <div
      className={cn('min-w-0 max-w-full overflow-auto rounded-lg border border-border bg-card', className)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table aria-label={label} aria-busy={loading || undefined} className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {columns.map((c, i) => {
              const sortierbar = c.sortable && onSortChange;
              const aktiv = sortierbar && sort?.key === c.key;
              const Symbol = !aktiv ? ChevronsUpDown : sort?.direction === 'asc' ? ArrowUp : ArrowDown;
              return (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  aria-sort={aktiv ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'sticky top-0 z-[1] whitespace-nowrap border-b border-border bg-card text-left t-label text-muted-foreground',
                    sortierbar ? 'p-0' : 'px-3 py-2.5 first:pl-4 last:pr-4',
                    c.align === 'end' && 'text-right',
                  )}
                >
                  {sortierbar ? (
                    // Knopf über die volle Kopfhöhe, nicht nur das Wort.
                    <button
                      type="button"
                      onClick={() => onSortChange({ key: c.key, direction: aktiv && sort?.direction === 'asc' ? 'desc' : 'asc' })}
                      className={cn(
                        'flex min-h-6 w-full cursor-pointer items-center gap-1 px-3 py-2.5 transition-colors duration-[120ms] hover:text-foreground',
                        i === 0 && 'pl-4',
                        i === columns.length - 1 && 'pr-4',
                        c.align === 'end' && 'justify-end',
                        aktiv && 'text-foreground',
                      )}
                    >
                      {c.header}
                      <Icon icon={Symbol} size={13} className={aktiv ? 'text-highlight' : 'opacity-60'} />
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: LADEZEILEN }, (_, z) => (
              <tr key={z} className="border-b border-border last:border-b-0">
                {columns.map((c) => (
                  <td key={c.key} className="h-row px-3 first:pl-4 last:pr-4">
                    <Skeleton className={cn('h-3.5', c.align === 'end' ? 'ml-auto w-16' : 'w-3/4')} />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="h-30 text-center text-muted-foreground">
                {empty ?? 'Keine Einträge'}
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const k = schluessel(r);
              return (
                <tr
                  key={k}
                  aria-selected={selectedKey != null ? k === selectedKey : undefined}
                  {...zeilenTasten(onRowClick, r)}
                  className={cn(
                    'border-b border-border transition-colors duration-100 last:border-b-0 hover:bg-accent aria-selected:bg-highlight-soft',
                    onRowClick && 'cursor-pointer',
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'h-row whitespace-nowrap px-3 first:pl-4 last:pr-4',
                        c.mono && 'font-mono text-[13px] tabular text-muted-foreground',
                        c.align === 'end' && 'text-right',
                        c.strong && 'font-medium',
                      )}
                    >
                      {zelle(r, c)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Ohne `mobil`-Angaben: erste Spalte als Titel, die nächsten zwei beschrifteten darunter. */
function plaetze<T>(columns: Column<T>[]) {
  if (!columns.some((c) => c.mobil)) {
    const [erste, ...rest] = columns;
    return { titel: erste, status: undefined, unter: rest.filter((c) => typeof c.header === 'string' && c.header).slice(0, 2), wert: undefined };
  }
  return {
    titel: columns.find((c) => c.mobil === 'titel'),
    status: columns.find((c) => c.mobil === 'status'),
    unter: columns.filter((c) => c.mobil === 'unter'),
    wert: columns.find((c) => c.mobil === 'wert'),
  };
}

function leer(n: ReactNode) {
  return n == null || n === false || n === '' || n === '–';
}

/** Handy: eine Zeile je Eintrag, sortieren über eine Auswahl darüber, kein seitliches Scrollen. */
function DataListe<T>({ columns, rows, rowKey, selectedKey, onRowClick, empty, label, sort, onSortChange, loading, className }: DataTableProps<T>) {
  const schluessel = schluesselVon(rowKey);
  const p = plaetze(columns);
  const mobilZelle = (r: T, c: Column<T>) => (c.renderMobil ? c.renderMobil(r) : zelle(r, c));
  const sortierbar = onSortChange ? columns.filter((c) => c.sortable) : [];

  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      {sortierbar.length > 0 && onSortChange && (
        <div className="flex items-center gap-2">
          <span aria-hidden className="shrink-0 t-small text-muted-foreground">
            Sortiert nach
          </span>
          <NativeSelect
            aria-label="Sortieren nach"
            placeholder="Auswählen"
            value={sort?.key ?? ''}
            options={sortierbar.map((c) => ({ value: c.key, label: kopfText(c) }))}
            onChange={(e) => onSortChange({ key: e.target.value, direction: 'asc' })}
            className="min-w-0 flex-1"
          />
          <IconButton
            icon={sort?.direction === 'desc' ? ArrowDownWideNarrow : ArrowUpNarrowWide}
            label={sort?.direction === 'desc' ? 'Absteigend sortiert, umkehren' : 'Aufsteigend sortiert, umkehren'}
            noTooltip
            disabled={!sort}
            onClick={() => sort && onSortChange({ key: sort.key, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
          />
        </div>
      )}
      {loading ? (
        <ul aria-label={label} aria-busy className="rounded-lg border border-border bg-card">
          {Array.from({ length: LADEZEILEN }, (_, z) => (
            <li key={z} className="flex min-h-row flex-col justify-center gap-2 border-b border-border px-4 py-3 last:border-b-0">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </li>
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-muted-foreground">{empty ?? 'Keine Einträge'}</div>
      ) : (
        <ul aria-label={label} className="rounded-lg border border-border bg-card">
          {rows.map((r) => {
            const k = schluessel(r);
            const status = p.status ? mobilZelle(r, p.status) : null;
            const unter = p.unter.map((c) => ({ key: c.key, inhalt: mobilZelle(r, c) })).filter((u) => !leer(u.inhalt));
            const wert = p.wert ? mobilZelle(r, p.wert) : null;
            return (
              <li
                key={k}
                data-selected={selectedKey != null && k === selectedKey ? '' : undefined}
                {...zeilenTasten(onRowClick, r)}
                className={cn(
                  'flex min-h-row items-center gap-3 border-b border-border px-4 py-2.5 transition-colors duration-100 last:border-b-0 data-[selected]:bg-highlight-soft',
                  onRowClick && 'cursor-pointer hover:bg-accent',
                )}
              >
                <div className="min-w-0 flex-1">
                  {p.titel && <div className={cn('truncate', p.titel.strong !== false && 'font-medium')}>{mobilZelle(r, p.titel)}</div>}
                  {(!leer(status) || unter.length > 0) && (
                    <div className="mt-0.5 flex min-w-0 items-center gap-2 t-small text-muted-foreground">
                      {!leer(status) && <span className="shrink-0">{status}</span>}
                      {unter.length > 0 && (
                        <span className="min-w-0 truncate">
                          {unter.map((u, i) => (
                            <Fragment key={u.key}>
                              {i > 0 && ' · '}
                              {u.inhalt}
                            </Fragment>
                          ))}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {!leer(wert) && <div className="shrink-0 text-right font-mono text-[13px] tabular">{wert}</div>}
                {onRowClick && <Icon icon={ChevronRight} className="-mr-1 text-muted-foreground" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
